import * as React from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const DISMISS_KEY = "pwa-install-dismissed";

/**
 * Batch E / M3 — compact install region (#15).
 * UA paths (A1/A5/#21): Chromium BIP → Telepítés CTA (`canPrompt`);
 * real iOS Safari only → Share copy (`showIosHint`). Never mix the two.
 * Dismiss persists in localStorage. Később / Telepítés ≥44px tap targets.
 */
export function InstallPrompt() {
  const { canPrompt, showIosHint, promptInstall, readyToShow } = useInstallPrompt();
  const [dismissed, setDismissed] = React.useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed || !readyToShow) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Alkalmazás telepítése"
      data-testid="pwa-install-banner"
      className="border-b border-slate-700 bg-slate-900 px-3 py-2 text-slate-50 dark:border-slate-600"
    >
      <div className="mx-auto flex max-w-5xl items-center gap-2 sm:gap-3">
        <div className="min-w-0 flex-1 text-xs leading-snug sm:text-sm">
          <span className="font-semibold">Takács PM — telepítés</span>
          {showIosHint ? (
            <span className="mt-0.5 block text-slate-300 sm:ml-2 sm:mt-0 sm:inline">
              <Share className="mr-0.5 inline h-3 w-3 align-text-bottom" aria-hidden />
              Megosztás → „Hozzáadás a Kezdőképernyőhöz”
            </span>
          ) : (
            <span className="mt-0.5 block text-slate-300 sm:ml-2 sm:mt-0 sm:inline">
              Kezdőképernyős app — jobb offline terepi használat
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {canPrompt ? (
            <Button
              type="button"
              variant="primary"
              size="default"
              onClick={() => void promptInstall()}
              data-testid="pwa-install-button"
            >
              <Download className="h-4 w-4 sm:mr-1.5" aria-hidden />
              <span className="hidden sm:inline">Telepítés</span>
              <span className="sm:hidden">Telepít</span>
            </Button>
          ) : null}
          {/* M3/A5: Később ≥44×44 — never size="sm" (h-9 / 36px) */}
          <Button
            type="button"
            variant="secondary"
            size="icon"
            data-testid="pwa-install-later"
            className="shrink-0"
            aria-label="Később"
            onClick={dismiss}
          >
            <X className="h-5 w-5" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
